/**
 * Binds a report's filter schema to the URL — CLAUDE.md §4.4. The query string is the single source
 * of truth: the page and `<FilterBar>` both call this and stay in step because neither holds state.
 */

import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import {
  applyFilterValues,
  clearFilterValues,
  describeFilters,
  hasActiveFilters,
  isLive,
  parseFilters,
  type FilterField,
  type FilterSchema,
  type FilterValues,
  type LiveOptions,
  type ParsedFilters,
} from '@/lib/filters';
import { fetchFilterOptions, type SortSpec } from '@/lib/api';
import { usePublishTabFacets } from '@/lib/reportWorkspace';

export interface ReportFilters extends ParsedFilters {
  /** Set one param. `null` removes it, so an unset filter leaves no trace in a shared link. */
  setValue: (param: string, value: string | null) => void;
  /** Set several at once — a date range is two params and should move as one navigation. */
  setValues: (changes: Partial<FilterValues>) => void;
  /** Drop every filter this schema owns. Params belonging to anything else are left alone. */
  clear: () => void;
  /** True when the URL sets at least one filter — the condition for offering "Clear filters". */
  isActive: boolean;
  /** The schema with every live option list filled in — what `<FilterBar>` renders. */
  schema: FilterSchema;
  /** Params whose options are still in flight. Their controls render disabled. */
  pendingOptions: ReadonlySet<string>;
}

/** Matched to the server's own option cache TTL. */
const OPTIONS_STALE_TIME = 10 * 60 * 1000;

function liveFields(schema: FilterSchema): { field: FilterField; live: LiveOptions }[] {
  return schema.flatMap((field) =>
    (field.type === 'select' || field.type === 'multi-select') && isLive(field.options)
      ? [{ field, live: field.options }]
      : [],
  );
}

export function useReportFilters(schema: FilterSchema): ReportFilters {
  const [search, setSearch] = useSearchParams();

  // Pass one: live fields validate provisionally, so a URL value survives until its options arrive.
  const provisional = useMemo(() => parseFilters(schema, search), [schema, search]);

  const live = useMemo(() => liveFields(schema), [schema]);

  const optionQueries = useQueries({
    queries: live.map(({ live: source }) => ({
      // The full value set is the key: the server decides which of them scope this list (§5.4).
      queryKey: ['filter-options', source.report, source.filter, provisional.values],
      queryFn: () => fetchFilterOptions(source.report, source.filter, provisional.values),
      staleTime: OPTIONS_STALE_TIME,
    })),
  });

  // Changes only when TanStack replaces a result, so the schema below keeps one identity across
  // ordinary re-renders — a fresh array every render would churn the callbacks reports depend on.
  const optionsVersion = optionQueries.map((query) => query.dataUpdatedAt).join('|');

  const resolvedSchema = useMemo<FilterSchema>(() => {
    if (live.length === 0) {
      return schema;
    }
    const byParam = new Map<string, readonly { value: string; label: string }[]>();
    live.forEach(({ field }, index) => {
      const data = optionQueries[index]?.data;
      if (data !== undefined) {
        byParam.set(field.name, data.options);
      }
    });
    if (byParam.size === 0) {
      return schema;
    }
    return schema.map((field) => {
      const options = byParam.get(field.name);
      return options !== undefined && (field.type === 'select' || field.type === 'multi-select')
        ? { ...field, options }
        : field;
    });
    // optionQueries is a fresh array each render; optionsVersion is what actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, live, optionsVersion]);

  const pendingOptions = useMemo<ReadonlySet<string>>(() => {
    const pending = new Set<string>();
    live.forEach(({ field }, index) => {
      if (optionQueries[index]?.data === undefined) {
        pending.add(field.name);
      }
    });
    return pending;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, optionsVersion]);

  // Pass two: the real membership check. A valid URL value yields exactly what pass one did, so the
  // request body is unchanged and the report does not re-query.
  const parsed = useMemo(
    () => (resolvedSchema === schema ? provisional : parseFilters(resolvedSchema, search)),
    [resolvedSchema, schema, provisional, search],
  );

  const setValues = useCallback(
    (changes: Partial<FilterValues>) => {
      // `replace`, not push: pushing would make Back walk filter by filter instead of leaving.
      setSearch((current) => applyFilterValues(schema, current, changes), { replace: true });
    },
    [schema, setSearch],
  );

  const setValue = useCallback(
    (param: string, value: string | null) => {
      setValues({ [param]: value });
    },
    [setValues],
  );

  const clear = useCallback(() => {
    setSearch((current) => clearFilterValues(schema, current), { replace: true });
  }, [schema, setSearch]);

  // What this instance is set to, for its tab in the strip. Here rather than in the workspace
  // because this is where option labels are resolved — a tab should say "Dhaka", not "F02".
  usePublishTabFacets(
    useMemo(() => describeFilters(resolvedSchema, parsed.values), [resolvedSchema, parsed.values]),
  );

  return {
    ...parsed,
    setValue,
    setValues,
    clear,
    isActive: hasActiveFilters(schema, search),
    schema: resolvedSchema,
    pendingOptions,
  };
}

/** The URL key the sort lives under. Exported so a page clearing it names it once, not twice. */
export const SORT_PARAM = 'sort';

export interface ReportSort {
  /** Pass straight to `<DataTable sort>` and into the export request. `/query` no longer takes it. */
  sort: SortSpec[];
  /** Pass straight to `<DataTable onSortChange>`. */
  setSort: (next: SortSpec[]) => void;
}

/**
 * Sort order, carried in the URL as `?sort=field:dir` (§4.4). `<DataTable>` applies it in the
 * browser (DECISIONS.md §30), so a shared link must carry it or the recipient sees other rows
 * first. **Nothing here checks the field against the report's `sort:` whitelist** — that governs
 * the export and which headers are offered, not the URL: an unknown field reaches `<DataTable>`,
 * is dropped by `getCanSort()`, and leaves the SQL fallback order on screen.
 */
export function useReportSort(defaultSort: SortSpec[] = [], param = SORT_PARAM): ReportSort {
  const [search, setSearch] = useSearchParams();

  const sort = useMemo<SortSpec[]>(() => {
    const raw = search.get(param);
    if (raw === null || raw.trim() === '') {
      return defaultSort;
    }

    const parsed = raw
      .split(',')
      .map((entry) => entry.split(':'))
      .flatMap<SortSpec>(([field, dir]) =>
        field !== undefined && field !== '' && (dir === 'asc' || dir === 'desc')
          ? [{ field, dir }]
          : [],
      );

    return parsed.length > 0 ? parsed : defaultSort;
  }, [search, defaultSort, param]);

  const setSort = useCallback(
    (next: SortSpec[]) => {
      setSearch(
        (current) => {
          const params = new URLSearchParams(current);
          if (next.length === 0) {
            params.delete(param);
          } else {
            params.set(param, next.map((spec) => `${spec.field}:${spec.dir}`).join(','));
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearch, param],
  );

  return { sort, setSort };
}
